import './style.scss'
import React, { useState } from 'react'
import { Flex, Text, tokens, UIProps } from 'src/componentv2/Base/ui'
import { Avatar } from 'src/componentv2/Base/Avatar'
import qoreIconDefault from 'src/assets/v2/img/qore_icon.svg'

export interface HeaderNavItem {
    label: string
    href?: string
    active?: boolean
    onClick?: () => void
}

const DEFAULT_NAV_ITEMS: HeaderNavItem[] = [
    { label: 'Institutions', active: true },
    { label: 'Audit Logs' },
    { label: 'Users' },
    { label: "FAQ's" },
    { label: 'Configuration' },
    { label: 'Surveys' },
]

export interface HeaderProps extends UIProps {
    logo?: React.ReactNode
    brandName?: React.ReactNode
    actions?: React.ReactNode
    avatar?: React.ReactNode
    avatarSrc?: string
    initials?: string
    avatarSize?: string
    height?: string
    paddingX?: string
    paddingY?: string
    sticky?: boolean
    /** `null` (default) = no nav row. `1` = show the standard nav items. */
    nav?: 1 | null
    navItems?: HeaderNavItem[]
}

export const Header: React.FC<HeaderProps> = ({
                                                  logo,
                                                  brandName,
                                                  actions,
                                                  avatar,
                                                  avatarSrc,
                                                  initials,
                                                  avatarSize = '4',
                                                  bgColor = tokens.white,
                                                  className = '',
                                                  height = '6.4rem',
                                                  paddingX = '13.5rem',
                                                  paddingY = '1.2rem',
                                                  sticky = false,
                                                  nav = null,
                                                  navItems = DEFAULT_NAV_ITEMS,
                                                  ...rest
                                              }) => {
    const [menuOpen, setMenuOpen] = useState(false)
    const showNav = nav === 1

    return (
        <Flex
            className={`header ${sticky ? 'header--sticky' : ''} ${className}`}
            width="100%"
            justify="space-between"
            items="center"
            bgColor={bgColor}
            style={{
                height,
                padding: `${paddingY} ${paddingX}`,
                boxSizing: 'border-box',
                position: sticky ? 'sticky' : 'static',
                top: sticky ? 0 : undefined,
                zIndex: sticky ? 1000 : undefined,
            }}
            {...rest}
        >
            <Flex className="header__brand" items="center" gap="0.8" style={{ height: '100%' }}>
                {logo ?? <img src={qoreIconDefault} alt="Qore" />}
                {brandName && (
                    <Text fontSize="2" fontWeight="700" color={tokens.textDark}>
                        {brandName}
                    </Text>
                )}
            </Flex>

            {showNav && (
                <nav className="header__nav">
                    {navItems.map((item) => (
                        <a
                            key={item.label}
                            className={`header__nav-link ${item.active ? 'active' : ''}`}
                            onClick={item.onClick}
                            href={item.href ?? '#'}
                        >
                            {item.label}
                        </a>
                    ))}
                </nav>
            )}

            <Flex className="header__actions" items="center" gap="1.6" style={{ height: '100%' }}>
                {actions}
                {avatar ?? ((avatarSrc || initials) && (
                    <Avatar src={avatarSrc} initials={initials} size={avatarSize} />
                ))}

                {showNav && (
                    <button
                        type="button"
                        className="header__burger"
                        aria-label="Toggle navigation"
                        onClick={() => setMenuOpen((v) => !v)}
                    >
                        <span /><span /><span />
                    </button>
                )}
            </Flex>

            {showNav && menuOpen && (
                <div className="header__mobile-nav">
                    {navItems.map((item) => (
                        <a
                            key={item.label}
                            className={`header__mobile-nav-link ${item.active ? 'active' : ''}`}
                            onClick={() => {
                                item.onClick?.()
                                setMenuOpen(false)
                            }}
                            href={item.href ?? '#'}
                        >
                            {item.label}
                        </a>
                    ))}
                </div>
            )}
        </Flex>
    )
}

export default Header